<script setup lang="ts">
import type { BreadcrumbProps } from '#seo-utils/app/composables/useBreadcrumbItems'
import type { SearchUrlParams } from '#shared/utils/search'
import { faFilter } from '@fortawesome/pro-light-svg-icons'
import { useRouteQuery } from '@vueuse/router'

useHead({
  bodyAttrs: {
    class: 'bg-moss-500',
  },
})

definePageMeta({
  colorMode: 'dark',
  theme: 'moss',
  layout: 'default',
  name: 'search-index',
  breadcrumb: {
    label: 'Søkeresultat',
  },
  middleware: ['validate-search-query-params'],
})

useSeoMeta({
  title: 'Søkeresultat',
  description: 'Søkeresultater for Juridika',
})

const openFilter = defineModel('openFilter', { default: false })
const route = useRoute()

const page = useRouteQuery<NonNullable<SearchUrlParams['page']>>('page', 1, {
  transform: (val) => {
    const num = Number(val)
    return Number.isNaN(num) || num < 1 ? 1 : Math.floor(num)
  },
})

const filterState = useSearchFilters()

const { searchLegalCategory, searchAuthor, sortOrder } = filterState

const searchState = useSearchState({ requestKind: 'full-results', page, filters: filterState.filters, sort: filterState.sort })

const { submit: submitSearch } = useSearchRouteCommit()

function setPage(p: number) {
  if (p >= 1 && p <= searchState.totalPages.value) {
    page.value = p
    window.scrollTo(0, 0)
  }
}

function handleSortChange(value: { value: string, label: string } | undefined) {
  const selectedSort = SORT_ORDER_OPTIONS.find(option => option.value === value?.value)
  if (!selectedSort || selectedSort.value === sortOrder.value.value) {
    return
  }

  sortOrder.value = selectedSort
  page.value = 1
}

const { facets: preloadedFacets } = useSearchFacetPreload()
const { contentTypeOptions, legalCategoryOptions, authorOptions } = useSearchFacetOptions(searchState.facets, preloadedFacets)

const filteredResults = useHighlightedResults(searchState.highlighting, searchState.searchResult, searchState.term)
const hasLicense = useHasLicense()
const searchDiagnosticFilterCount = computed(() => {
  const filters = filterState.filters.value
  return filters.contentTypes.length
    + filters.legalAreas.length
    + filters.authors.length
    + Number(Boolean(filters.startYear))
    + Number(Boolean(filters.endYear))
})
const searchDiagnosticFilterSignature = computed(() => {
  const filters = filterState.filters.value
  return JSON.stringify({
    contentTypes: [...filters.contentTypes].sort(),
    legalAreas: [...filters.legalAreas].sort(),
    authors: [...filters.authors].sort(),
    startYear: filters.startYear ?? null,
    endYear: filters.endYear ?? null,
  })
})

const searchDiagnostics = useSearchDiagnosticsAnalytics({
  query: searchState.term,
  page,
  routeSearchSignature: computed(() => route.fullPath),
  isLoading: searchState.isLoading,
  results: filteredResults,
  totalResults: searchState.totalResults,
  rows: ITEMS_PER_PAGE,
  hasLicense,
  transportRetried: searchState.transportRetried,
  hasError: searchState.hasError,
  errorStatus: searchState.errorStatus,
  filterCount: searchDiagnosticFilterCount,
  filterSignature: searchDiagnosticFilterSignature,
  sortOrder: computed(() => sortOrder.value.value),
})

useSearchHistoryTracker('global', searchState.term, searchState.searchResult, searchState.isLoading)

const displayTipsAndEmptyState = computed(() => {
  return !searchState.searchResult.value.length && !searchState.isLoading.value
})
const didYouMeanSuggestion = computed(() => {
  if (searchState.totalResults.value > 0 || searchState.isLoading.value) {
    return ''
  }

  return searchState.spellcheckSuggestion.value ?? ''
})

const breadcrumbOverrides = computed(() => {
  return [
    undefined,
    {
      label: 'Søkeresultat',
      to: '/sokeresultat',
    },
  ] satisfies BreadcrumbProps['overrides']
})

function handleSpellcheckSelect(query: string) {
  submitSearch(query)
}

</script>

<template>
  <ContentDiv>
    <Breadcrumbs :overrides="breadcrumbOverrides" />

    <section class="pb-2xl mx-auto sticky">
      <div class="flex flex-col gap-s mx-auto max-w-2xl xl:max-w-4xl min-h-[106px]">
        <SearchGlobalInput
          placeholder="Søk i hele Juridika"
          :content-type-options="contentTypeOptions"
          :search-results="filteredResults"
          :total-results="searchState.totalResults.value"
          :is-loading="searchState.isLoading.value"
          :toggle-content-type="filterState.toggleContentType"
          :selected-content-types="filterState.selectedContentTypes.value"
          :reset-content-type="filterState.resetContentType"
          class="w-full max-w-[837px]"
        />
        <SearchFilterChipList />
      </div>
    </section>

    <section class="flex flex-col bg-white rounded-4xl py-2xl px-m -mx-m">
      <div class="w-full max-w-screen-xl mx-auto flex gap-[40px]">
        <div class="hidden xl:flex flex-col items-start gap-l pb-s px-s">
          <span class="text-body font-bold">Filtrer søket</span>
          <SearchFilters
            v-model:search-legal-category="searchLegalCategory"
            v-model:search-author="searchAuthor"
            :content-type-options="contentTypeOptions"
            :legal-category-options="legalCategoryOptions"
            :author-options="authorOptions"
          />
        </div>
        <div class="flex-1 flex flex-col">
          <div class="flex flex-col items-start gap-s">
            <div v-if="searchState.term.value" class="flex items-center justify-between w-full">
              <Skeleton v-if="searchState.isLoading.value" class="h-4 w-10" />
              <span v-else class="text-body font-bold">
                {{ searchState.totalResults.value }} treff
              </span>
              <Skeleton v-if="searchState.isLoading.value" class="h-6 w-30" />
              <SearchResultCombobox
                v-else
                id="search-result-sort-combobox"
                :model-value="sortOrder"
                label="Sorter etter"
                placeholder="Velg sortering"
                class="hidden xl:flex w-full max-w-[160px]"
                @update:model-value="handleSortChange"
              />
              <Chip
                size="small"
                variant="filled"
                color="carbon"
                :icon-left="faFilter"
                class="xl:hidden"
                aria-label="Filtrer søket"
                @click="openFilter = true"
              >
                Filtrer
              </Chip>
            </div>
            <template v-if="searchState.isLoading.value">
              <SearchLoadingSkeletonItem v-for="i in 10" :key="i" />
            </template>
            <SearchDidYouMean
              v-if="didYouMeanSuggestion"
              :suggestion="didYouMeanSuggestion"
              @select="handleSpellcheckSelect"
            />
            <SearchTipsAndEmpty v-if="displayTipsAndEmptyState" :search-term="searchState.term.value" />
            <SearchResultItem
              v-for="(item, index) in filteredResults"
              :key="index"
              :result="item"
              :is-loading="!filteredResults.length"
              :search-query="searchState.term.value"
              @click-result="searchDiagnostics.trackResultClicked(item, index + 1)"
            />
          </div>
          <div v-if="searchState.totalResults.value > ITEMS_PER_PAGE" class="my-2xl">
            <Pagination
              class="flex justify-center"
              :item-count="searchState.totalResults.value"
              :items-per-page="ITEMS_PER_PAGE"
              :current-page="page"
              @go-to-page="setPage"
            />
          </div>
        </div>
      </div>

      <SearchMobileFilters
        v-model:open-filter="openFilter"
        v-model:search-legal-category="searchLegalCategory"
        v-model:search-author="searchAuthor"
        class="xl:hidden"
        :content-type-options="contentTypeOptions"
        :legal-category-options="legalCategoryOptions"
        :author-options="authorOptions"
        :original-total-results="searchState.totalResults.value"
        :change-sort-order="handleSortChange"
      />
    </section>
  </ContentDiv>
</template>
